/**
 * Custom hook for transaction filtering logic
 * Extracted from Activity.tsx to improve code organization
 */

import { useMemo } from "react";
import { TransactionWithDate } from "../../../utils/transactionProcessing";
import { Account, TransactionCategory, TransactionCategoryGroup } from "../../../types";
import { isBypassAuthEnabled } from "../../../utils/bypassAuth";
import { getTransactionFriendlyId } from "../../../utils/transactionUtils";
import { WorkspaceMember } from "./useWorkspaceData";

const TRANSACTION_TYPES: readonly string[] = ["All", "Expenses", "Transfer", "Income", "Adjustment"] as const;

export interface UseTransactionFiltersParams {
  // Filter values
  transactions: TransactionWithDate[];
  searchQuery: string;
  typeFilter: string;
  groupFilter: string;
  categoryFilter: string;
  startDate: string;
  endDate: string;
  selectedUsers: string[];
  selectedAccountFilters: string[];
  showOnlyEdited: boolean;
  /** When set, search also matches user-friendly transaction ID (e.g. DV-1) */
  transactionIdPrefix?: string;
  
  // Data dependencies
  accounts: Account[];
  categoryGroups: TransactionCategoryGroup[];
  expenseCategories: TransactionCategory[];
  incomeCategories: TransactionCategory[];
  transactionHasHistory: Set<string>;
  workspaceMembers: WorkspaceMember[];
  workspaceOwnerId: string | null;
  /** When true (demo workspace), always show transactions from demo-user and workspace owner regardless of user filter */
  isDemoWorkspace?: boolean;

  // Helper function
  getAccountName: (accountId: string | undefined, storedAccountName?: string) => string;
}

export interface UseTransactionFiltersResult {
  filteredTransactions: TransactionWithDate[];
  baseFilteredTransactions: TransactionWithDate[];
  availableTypes: readonly string[];
  availableGroups: TransactionCategoryGroup[];
  availableCategories: string[];
}

/**
 * Hook for filtering transactions and computing available filter options
 */
export const useTransactionFilters = (params: UseTransactionFiltersParams): UseTransactionFiltersResult => {
  const {
    transactions,
    searchQuery,
    typeFilter,
    groupFilter,
    categoryFilter,
    startDate,
    endDate,
    selectedUsers,
    selectedAccountFilters,
    showOnlyEdited,
    transactionIdPrefix,
    accounts,
    categoryGroups,
    expenseCategories,
    incomeCategories,
    transactionHasHistory,
    workspaceMembers,
    workspaceOwnerId,
    isDemoWorkspace = false,
    getAccountName,
  } = params;

  // Get transactions that match current filters (excluding type/group/category for cascading filter logic)
  const baseFilteredTransactions = useMemo(() => {
    return transactions.filter((transaction) => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase().trim();
        const friendlyId = getTransactionFriendlyId(transactionIdPrefix, transaction.friendlyIdSequence);
        const matchesFriendlyId = friendlyId != null && friendlyId.toLowerCase().includes(query);
        if (transaction.type === "transfer") {
          const fromAccountName = getAccountName(transaction.fromAccountId, transaction.fromAccountName)?.toLowerCase() || "";
          const toAccountName = getAccountName(transaction.toAccountId, transaction.toAccountName)?.toLowerCase() || "";
          const matchesSearch =
            matchesFriendlyId ||
            fromAccountName.includes(query) ||
            toAccountName.includes(query) ||
            transaction.note?.toLowerCase().includes(query);
          if (!matchesSearch) return false;
        } else if (transaction.type === "adjustment") {
          const accountName = getAccountName(transaction.accountId, transaction.accountName)?.toLowerCase() || "";
          const matchesSearch =
            matchesFriendlyId ||
            accountName.includes(query) ||
            transaction.note?.toLowerCase().includes(query);
          if (!matchesSearch) return false;
        } else {
          const matchesSearch =
            matchesFriendlyId ||
            (transaction.title?.toLowerCase() || "").includes(query) ||
            (transaction.category?.toLowerCase() || "").includes(query) ||
            (transaction.note?.toLowerCase() || "").includes(query);
          if (!matchesSearch) return false;
        }
      }

      // Date range filter - use local time boundaries (YYYY-MM-DD as UTC causes timezone bugs)
      // e.g. new Date("2025-02-15") = UTC midnight = excludes early-morning "today" in UTC+ zones
      if (startDate || endDate) {
        const transactionDate = transaction.date;
        if (startDate) {
          const [y, m, d] = startDate.split("-").map(Number);
          const rangeStart = new Date(y, m - 1, d, 0, 0, 0, 0); // local midnight
          if (transactionDate < rangeStart) return false;
        }
        if (endDate) {
          const [y, m, d] = endDate.split("-").map(Number);
          const rangeEnd = new Date(y, m - 1, d, 23, 59, 59, 999); // local end of day
          if (transactionDate > rangeEnd) return false;
        }
      }

      // User filter
      // If selectedUsers is empty, show all transactions (no filter)
      // If selectedUsers has items, only show transactions from selected users
      if (selectedUsers.length > 0 && transaction.userId) {
        // Demo workspace: always show transactions from demo-user and workspace owner so demo content is visible
        if (isDemoWorkspace && (transaction.userId === "demo-user" || (workspaceOwnerId && transaction.userId === workspaceOwnerId))) {
          // Include – skip the rest of user filter
        } else if (isBypassAuthEnabled()) {
          // Check if transaction has owner ID (use workspaceOwnerId directly for reliability)
          if (workspaceOwnerId && transaction.userId === workspaceOwnerId) {
            // Transaction has owner ID - always include in bypass auth mode
            // Skip the rest of the user filter logic - continue to account/edited filters
            // (owner is the developer, their transactions should always be visible)
          } else {
            // Not owner ID, continue with normal filtering below
            // In bypass auth mode, if owner is selected, also include legacy user IDs
            // This ensures old transactions (with bypass-user, demo-user) are shown
            // Note: dev-user-developer is replaced with bypass-user
            const isLegacyUserId = (userId: string): boolean => {
              return ["bypass-user", "demo-user"].includes(userId);
            };
            
            // Check if transaction userId is directly selected
            if (selectedUsers.includes(transaction.userId)) {
              // Transaction is selected, include it
            } else if (isLegacyUserId(transaction.userId)) {
              // Legacy ID - check if owner is selected (legacy IDs should map to owner)
              // Use workspaceOwnerId directly if available, otherwise find from workspaceMembers
              const ownerId = workspaceOwnerId || workspaceMembers.find((m) => m.role === "owner")?.id;
              if (ownerId && selectedUsers.includes(ownerId)) {
                // Owner is selected, include legacy ID transactions
              } else {
                // Owner not selected, exclude legacy ID transactions
                return false;
              }
            } else {
              // Transaction userId not selected, not owner ID, and not legacy ID - exclude it
              return false;
            }
          }
        } else {
          // Not bypass auth mode - normal filtering (also allow demo-user when on demo workspace)
          const isDemoOrOwner = isDemoWorkspace && (transaction.userId === "demo-user" || (workspaceOwnerId && transaction.userId === workspaceOwnerId));
          if (!isDemoOrOwner && !selectedUsers.includes(transaction.userId)) {
            return false;
          }
        }
      }

      // Account filter
      if (selectedAccountFilters.length > 0 && transaction.accountId) {
        if (!selectedAccountFilters.includes(transaction.accountId)) return false;
      }

      // Edited filter
      if (showOnlyEdited && !transactionHasHistory.has(transaction.id)) {
        return false;
      }

      return true;
    });
  }, [transactions, searchQuery, startDate, endDate, selectedUsers, selectedAccountFilters, showOnlyEdited, transactionIdPrefix, transactionHasHistory, workspaceMembers, workspaceOwnerId, isDemoWorkspace, getAccountName]);

  // Filter transactions with all filters applied (including type/group/category)
  const filteredTransactions = useMemo(() => {
    const filtered = baseFilteredTransactions.filter((transaction) => {
      // Type filter
      if (typeFilter !== "All") {
        if (typeFilter === "Transfer" && transaction.type !== "transfer") return false;
        if (typeFilter === "Income" && transaction.type !== "income") return false;
        if (typeFilter === "Expenses" && transaction.type !== "expense") return false;
        if (typeFilter === "Adjustment" && transaction.type !== "adjustment") return false;
      }

      // Group filter (only for expenses/income, not transfers/adjustments)
      if (groupFilter !== "All" && transaction.type !== "transfer" && transaction.type !== "adjustment") {
        const selectedGroup = categoryGroups.find((g) => g.name === groupFilter);
        if (selectedGroup) {
          // Find the category that matches this transaction's category name
          const transactionCategory = expenseCategories.find((cat) => cat.name === transaction.category) ||
                                      incomeCategories.find((cat) => cat.name === transaction.category);
          // Check if the category belongs to the selected group
          if (!transactionCategory || transactionCategory.groupId !== selectedGroup.id) {
            return false;
          }
        }
      }

      // Category filter (only for expenses/income, not transfers/adjustments)
      if (categoryFilter !== "All" && transaction.type !== "transfer" && transaction.type !== "adjustment" && transaction.category !== categoryFilter) {
        return false;
      }

      return true;
    });
    return filtered;
  }, [baseFilteredTransactions, typeFilter, groupFilter, categoryFilter, categoryGroups, expenseCategories, incomeCategories]);

  // Get available types - filter by group and category if selected, but always include selected type
  // Remove "Transfer" and "Adjustment" when group or category is selected (they don't have groups/categories)
  const availableTypes = useMemo(() => {
    if (groupFilter === "All" && categoryFilter === "All") {
      return TRANSACTION_TYPES;
    }

    const typeSet = new Set<string>();
    typeSet.add("All"); // Always include "All"
    
    // Always include the selected type if one is selected
    if (typeFilter !== "All") {
      typeSet.add(typeFilter);
    }

    baseFilteredTransactions.forEach((transaction) => {
      // Apply group filter if set
      if (groupFilter !== "All" && transaction.type !== "transfer" && transaction.type !== "adjustment") {
        const selectedGroup = categoryGroups.find((g) => g.name === groupFilter);
        if (selectedGroup) {
          const transactionCategory = expenseCategories.find((cat) => cat.name === transaction.category) ||
                                      incomeCategories.find((cat) => cat.name === transaction.category);
          if (!transactionCategory || transactionCategory.groupId !== selectedGroup.id) {
            return; // Skip this transaction
          }
        }
      }

      // Apply category filter if set
      if (categoryFilter !== "All" && transaction.type !== "transfer" && transaction.type !== "adjustment") {
        if (transaction.category !== categoryFilter) {
          return; // Skip this transaction
        }
      }

      // Add the transaction type (only expenses and income can have groups/categories)
      if (transaction.type === "income") {
        typeSet.add("Income");
      } else if (transaction.type === "expense") {
        typeSet.add("Expenses");
      }
      // Note: We don't add "Transfer" or "Adjustment" here because they don't have groups/categories
    });

    // Filter out "Transfer" and "Adjustment" when group or category is selected
    // (unless they are currently selected, in which case we keep them visible)
    return TRANSACTION_TYPES.filter((type) => {
      // If group or category is selected, exclude Transfer and Adjustment
      if ((groupFilter !== "All" || categoryFilter !== "All") && (type === "Transfer" || type === "Adjustment")) {
        // But keep it if it's the currently selected type
        return type === typeFilter;
      }
      // Otherwise, include if it's in our set
      return typeSet.has(type);
    });
  }, [baseFilteredTransactions, groupFilter, categoryFilter, typeFilter, categoryGroups, expenseCategories, incomeCategories]);

  // Get available groups - filter by type if selected, but always include selected group
  const availableGroups = useMemo(() => {
    if (typeFilter === "All") {
      return categoryGroups;
    }

    const groupIdSet = new Set<string>();
    
    // Always include the selected group if one is selected
    if (groupFilter !== "All") {
      const selectedGroup = categoryGroups.find((g) => g.name === groupFilter);
      if (selectedGroup) {
        groupIdSet.add(selectedGroup.id);
      }
    }

    baseFilteredTransactions.forEach((transaction) => {
      // Apply type filter if set
      if (typeFilter !== "All") {
        if (typeFilter === "Transfer" && transaction.type !== "transfer") return;
        if (typeFilter === "Income" && transaction.type !== "income") return;
        if (typeFilter === "Expenses" && transaction.type !== "expense") return;
        if (typeFilter === "Adjustment" && transaction.type !== "adjustment") return;
      }

      // Only process expenses/income (groups don't apply to transfers/adjustments)
      if (transaction.type !== "transfer" && transaction.type !== "adjustment" && transaction.category) {
        const transactionCategory = expenseCategories.find((cat) => cat.name === transaction.category) ||
                                    incomeCategories.find((cat) => cat.name === transaction.category);
        if (transactionCategory && transactionCategory.groupId) {
          groupIdSet.add(transactionCategory.groupId);
        }
      }
    });

    return categoryGroups.filter((group) => groupIdSet.has(group.id));
  }, [baseFilteredTransactions, typeFilter, groupFilter, categoryGroups, expenseCategories, incomeCategories]);

  // Get available categories - filter by type and group if selected, but always include selected category
  const availableCategories = useMemo(() => {
    const categoryNameSet = new Set<string>();
    
    // Always include the selected category if one is selected
    if (categoryFilter !== "All") {
      categoryNameSet.add(categoryFilter);
    }

    baseFilteredTransactions.forEach((transaction) => {
      // Apply type filter if set
      if (typeFilter !== "All") {
        if (typeFilter === "Transfer" && transaction.type !== "transfer") return;
        if (typeFilter === "Income" && transaction.type !== "income") return;
        if (typeFilter === "Expenses" && transaction.type !== "expense") return;
        if (typeFilter === "Adjustment" && transaction.type !== "adjustment") return;
      }

      // Apply group filter if set
      if (groupFilter !== "All" && transaction.type !== "transfer" && transaction.type !== "adjustment") {
        const selectedGroup = categoryGroups.find((g) => g.name === groupFilter);
        if (selectedGroup) {
          const transactionCategory = expenseCategories.find((cat) => cat.name === transaction.category) ||
                                      incomeCategories.find((cat) => cat.name === transaction.category);
          if (!transactionCategory || transactionCategory.groupId !== selectedGroup.id) {
            return; // Skip this transaction
          }
        }
      }

      // Only process expenses/income (categories don't apply to transfers/adjustments)
      if (transaction.type !== "transfer" && transaction.type !== "adjustment" && transaction.category) {
        categoryNameSet.add(transaction.category);
      }
    });

    return Array.from(categoryNameSet).sort();
  }, [baseFilteredTransactions, typeFilter, groupFilter, categoryFilter, categoryGroups, expenseCategories, incomeCategories]);

  return {
    filteredTransactions,
    baseFilteredTransactions,
    availableTypes,
    availableGroups,
    availableCategories,
  };
};
